/**
 * components/viewer/MeasurementStationOverlay.jsx — Iter 50.
 *
 * Renders STA / Offset annotations as DOM chips positioned over each
 * persisted measurement. Coordinate + distance measurements get inline
 * station labels (start & end for distance, single for coordinate).
 *
 * Why a sibling DOM overlay and not enrich the existing SVG label?
 *   - Keeps the measurement-tap-to-delete UX intact.
 *   - StationChip already handles caching + null-when-no-alignment.
 *   - Annotations render only when the sheet HAS an alignment and the
 *     resolver succeeds; otherwise zero added paint cost.
 *
 * NO new measurement types. NO edit. View only.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StationChip from "@/components/object/StationChip";

export default function MeasurementStationOverlay({ sheetId, reloadKey }) {
  const [rows, setRows] = useState([]);
  const [hasAlignment, setHasAlignment] = useState(false);

  useEffect(() => {
    if (!sheetId) { setRows([]); setHasAlignment(false); return; }
    let cancel = false;
    (async () => {
      try {
        const [{ data: alns }, { data: ms }] = await Promise.all([
          api.get(`/sheets/${sheetId}/alignments`),
          api.get(`/sheets/${sheetId}/measurements`),
        ]);
        if (cancel) return;
        const has = (alns?.alignments?.length || 0) > 0;
        setHasAlignment(has);
        setRows(has ? (ms || []) : []);
      } catch {
        if (!cancel) { setRows([]); setHasAlignment(false); }
      }
    })();
    return () => { cancel = true; };
  }, [sheetId, reloadKey]);

  if (!hasAlignment || rows.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none"
          data-testid="measurement-station-overlay">
      {rows.map((m) => <Annotations key={m.id} m={m} sheetId={sheetId} />)}
    </div>
  );
}

function Annotations({ m, sheetId }) {
  const pts = m.points || [];
  if (!pts.length) return null;
  // Coordinate (single-point): chip below the point.
  if (m.kind === "coordinate") {
    const p = pts[0];
    return (
      <ChipAt nx={p.nx} ny={p.ny} offsetY={18}
               testid={`measurement-station-${m.id}`}>
        <StationChip sheetId={sheetId} nx={p.nx} ny={p.ny} compact
                       testId={`measurement-station-chip-${m.id}`} />
      </ChipAt>
    );
  }
  // Distance: start + end chips.
  if (m.kind === "distance" && pts.length >= 2) {
    const a = pts[0], b = pts[1];
    return (
      <>
        <ChipAt nx={a.nx} ny={a.ny} offsetY={-22}
                 testid={`measurement-station-start-${m.id}`}>
          <StationChip sheetId={sheetId} nx={a.nx} ny={a.ny} compact
                         testId={`measurement-station-chip-start-${m.id}`} />
        </ChipAt>
        <ChipAt nx={b.nx} ny={b.ny} offsetY={-22}
                 testid={`measurement-station-end-${m.id}`}>
          <StationChip sheetId={sheetId} nx={b.nx} ny={b.ny} compact
                         testId={`measurement-station-chip-end-${m.id}`} />
        </ChipAt>
      </>
    );
  }
  // Polyline: annotate first + last only (keeps the canvas calm).
  if (m.kind === "polyline" && pts.length >= 2) {
    const a = pts[0], b = pts[pts.length - 1];
    return (
      <>
        <ChipAt nx={a.nx} ny={a.ny} offsetY={-22}
                 testid={`measurement-station-start-${m.id}`}>
          <StationChip sheetId={sheetId} nx={a.nx} ny={a.ny} compact
                         testId={`measurement-station-chip-start-${m.id}`} />
        </ChipAt>
        <ChipAt nx={b.nx} ny={b.ny} offsetY={-22}
                 testid={`measurement-station-end-${m.id}`}>
          <StationChip sheetId={sheetId} nx={b.nx} ny={b.ny} compact
                         testId={`measurement-station-chip-end-${m.id}`} />
        </ChipAt>
      </>
    );
  }
  return null;
}

function ChipAt({ nx, ny, offsetY = 0, testid, children }) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${nx * 100}%`,
        top: `${ny * 100}%`,
        transform: `translate(-50%, ${offsetY}px)`,
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}
